import { Loader2, Send } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { cn } from "@/lib/utils";
import { aiChat } from "@/services/ai";

import { useAiPanelStore, type AiPanelBridge } from "./ai-panel-store";

export function ChatMode(props: AiPanelBridge) {
  // Chat 模式不依赖 savedAppId/onAppCreated，保留 props 以维持与其他模式一致的签名
  void props;
  const messages = useAiPanelStore((state) => state.chatMessages);
  const appendChatMessage = useAiPanelStore((state) => state.appendChatMessage);
  const [input, setInput] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const bottomRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const handleSend = async (text: string) => {
    if (!text.trim() || loading) return;
    setInput("");
    appendChatMessage({ role: "user", content: text });
    setLoading(true);
    try {
      const result = await aiChat(text, "dev-assistant");
      const response = result.response;
      const content =
        typeof response.content === "string" ? response.content : JSON.stringify(response, null, 2);
      appendChatMessage({ role: "assistant", content, source: result.source });
    } catch (error) {
      appendChatMessage({ role: "assistant", content: `请求失败：${String(error)}`, source: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-2 overflow-auto p-3" data-testid="chat-messages">
        {messages.map((message, idx) => (
          <div key={idx} className={cn("flex", message.role === "user" ? "justify-end" : "justify-start")}>
            <div
              className={cn(
                "max-w-[90%] whitespace-pre-wrap rounded-lg px-3 py-2 text-xs",
                message.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted",
              )}
            >
              {message.content}
              {message.source && <div className="mt-1 text-[10px] opacity-60">来源：{message.source}</div>}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="flex items-center gap-1.5 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              思考中...
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="border-t bg-card p-3">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="问我任何问题，例如：如何给按钮添加跳转动作？"
          className="min-h-[48px] resize-none text-sm"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleSend(input);
            }
          }}
        />
        <Button
          className="mt-2 w-full"
          size="sm"
          disabled={loading || !input.trim()}
          onClick={() => void handleSend(input)}
          data-testid="chat-send"
        >
          <Send className="mr-1.5 h-4 w-4" />
          发送
        </Button>
      </div>
    </div>
  );
}
